// Single source of truth for pricing (USD, per unit).
// Annual = 10 months paid for 12 => 2 months free (~16.7% off).
export const ANNUAL_FREE_MONTHS = 2;

const annualFromMonthly = (monthly: number) =>
  Math.round(monthly * (12 - ANNUAL_FREE_MONTHS) * 100) / 100;

export const TIER_PRICING = {
  'Starter': {
    unitPrice: 34.99,
    unitPriceAnnual: annualFromMonthly(34.99),
    features: ['Digital HACCP logs with corrective actions', 'One-click audit reports (PDF/Excel) for inspectors', 'Automatic law updates for your region', '7-day free trial', 'Email support (72h response)']
  },
  'Growth': {
    unitPrice: 49.99,
    unitPriceAnnual: annualFromMonthly(49.99),
    features: ['Group-wide compliance dashboard (all units in one view)', 'Role-based staff access (limit who can edit vs. review)', 'Advanced reporting & analytics to spot weak points', 'Live regulation sync per location/region', 'Priority email support (48h response)']
  },
  'Pro': {
    unitPrice: 59.99,
    unitPriceAnnual: annualFromMonthly(59.99),
    features: ['Regional compliance presets (auto-apply local rules)', 'Customizable checklists & workflows for unique operations', 'Full audit history & export filters (prove compliance anytime)', 'Standard email support (48–72h response)']
  }
} as const;

export type TierName = keyof typeof TIER_PRICING;

// Effective discount rate, derived from the free-months model
export const ANNUAL_DISCOUNT = ANNUAL_FREE_MONTHS / 12;

const round2 = (n: number) => Math.round(n * 100) / 100;

export const calculateTierCost = (tier: TierName, units: number, isAnnual = false) => {
  const { unitPrice, unitPriceAnnual } = TIER_PRICING[tier];

  const monthlyTotal = round2(unitPrice * units);
  const annualTotal = round2(unitPriceAnnual * units);
  const annualListTotal = round2(unitPrice * 12 * units);
  const annualSavings = round2(annualListTotal - annualTotal);

  return {
    unitPrice,
    unitPriceAnnual,
    // Effective per-unit monthly cost on the annual plan
    monthlyPerUnit: isAnnual ? round2(unitPriceAnnual / 12) : unitPrice,
    units,
    monthlyTotal,
    annualTotal,
    annualListTotal,
    annualSavings,
    // Amount actually charged for the chosen interval
    finalTotal: isAnnual ? annualTotal : monthlyTotal,
  };
};

export const getUnitPrice = (units: number, selectedTier?: TierName) => {
  const tier = selectedTier || 'Starter';
  const pricing = calculateTierCost(tier, units);
  return {
    price: pricing.monthlyTotal,
    tier,
    isEnterprise: false
  };
};

export const formatMoney = (amount: number, currency = 'USD', decimals?: number) => {
  const fraction = decimals ?? (Number.isInteger(amount) ? 0 : 2);
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: fraction,
    maximumFractionDigits: fraction,
  }).format(amount);
};